import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Container from '@/components/ui/container'
import Link from '@/components/ui/link'
import SectionHeader from '@/components/ui/section-header'

const ARTICLE_IMAGES = [
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-1.png',
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-2.png',
  'https://bravic-public-prod.s3.us-east-2.amazonaws.com/article-3.png',
]

export default function Insights() {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()
  const [activeIndex, setActiveIndex] = useState(0)

  const articles = t('insights.articles', { returnObjects: true }) as {
    slug: string
    category: string
    title: string
    description: string
  }[]

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % articles.length)
  }, [articles.length])

  useEffect(() => {
    const timer = setInterval(next, 5000)
    return () => clearInterval(timer)
  }, [next, activeIndex])

  const activeArticle = articles[activeIndex]

  return (
    <section ref={ref} id="insights" className="py-16 md:py-24 lg:py-32 bg-surface">
      <Container>
        <div
          className={`transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader
            label={t('insights.label')}
            heading={t('insights.heading')}
            className="mb-10 md:mb-16 lg:mb-20"
          />
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Featured Article */}
          <Link
            href={`/article/${activeArticle?.slug}`}
            className={`col-span-12 lg:col-span-7 group cursor-pointer relative overflow-hidden bg-surface-container transition-all duration-700 ease-out ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
            style={{ transitionDelay: visible ? '200ms' : '0ms' }}
          >
            <div className="h-[280px] sm:h-[350px] md:h-[400px] lg:h-[500px] overflow-hidden">
              <img
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 opacity-60"
                src={ARTICLE_IMAGES[activeIndex]}
                alt={activeArticle?.title}
              />
            </div>
            <div className="absolute inset-0 bg-linear-to-t from-black to-transparent p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col justify-end">
              <span className="text-primary font-bold text-xs mb-4">{activeArticle?.category}</span>
              <h3 className="font-display text-3xl font-bold text-white mb-4 group-hover:text-primary transition-colors">
                {activeArticle?.title}
              </h3>
              <p className="text-white/70 text-sm mb-6 max-w-lg line-clamp-2">
                {activeArticle?.description}
              </p>
              <div className="flex items-center gap-4">
                <span className="h-px w-12 bg-primary" />
                <span className="text-white text-xs font-bold tracking-widest uppercase">
                  {t('insights.readMore')}
                </span>
              </div>
            </div>
          </Link>

          {/* Side Articles */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            {articles.map((article, i) => (
              <div
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`bg-surface-container-high p-5 sm:p-6 md:p-8 hover:bg-surface-container-highest transition-all duration-500 ease-out cursor-pointer border-l-2 ${
                  visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                } ${
                  i === activeIndex
                    ? 'border-primary bg-surface-container-highest'
                    : 'border-transparent hover:border-primary/40'
                }`}
                style={{ transitionDelay: visible ? `${300 + i * 150}ms` : '0ms' }}
              >
                <span className="text-outline text-xs font-bold mb-2 block uppercase tracking-tighter">
                  {article.category}
                </span>
                <h4 className="font-display text-xl font-bold">{article.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}
